import {
  addressSchema,
  defaultProvinceCode,
  withProvince,
} from "@babackapp/return-widget-components/forms/address.form";
import type { ShippingAddress } from "@babackapp/return-widget-components/types";
import { deriveCountryName } from "@babackapp/return-widget-components/utils/countries";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useShouldPayReshipping } from "@babackapp/return-widget-components/hooks/use-should-pay-reshipping";
import { useSetAddress } from "@babackapp/return-widget-components/hooks/use-set-address";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

export function useAddressForm() {
  const navigate = useNavigate();
  const { orderId, shopId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const setAddress = useSetAddress(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const shouldPayReshipping = useShouldPayReshipping(orderId, shopId);

  const defaultAddress = order?.shippingAddress;

  const form = useForm({
    resolver: zodResolver(addressSchema),
    // useFocusFirstError focuses the field once its message is tied (BBS-438).
    shouldFocusError: false,
    defaultValues: {
      companyName: defaultAddress?.companyName ?? "",
      first_name: defaultAddress?.first_name ?? "",
      last_name: defaultAddress?.last_name ?? "",
      address1: defaultAddress?.address1 ?? "",
      address2: defaultAddress?.address2 ?? "",
      city: defaultAddress?.city ?? "",
      zip: defaultAddress?.zip ?? "",
      country_code: defaultAddress?.country_code ?? "",
      email: defaultAddress?.email ?? "",
      phone: defaultAddress?.phone ?? "",
      province_code: defaultProvinceCode(defaultAddress),
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    setAddress({
      ...withProvince(values, defaultAddress),
      country: deriveCountryName(values.country_code),
    } as ShippingAddress);
    const needsPayment =
      financialSummary.amountDue > 102 || shouldPayReshipping;
    navigate(needsPayment ? "../checkout" : "../complete");
  });

  return {
    form,
    onSubmit,
    userPortalTexts: shop?.preferences?.userPortal?.formAddress,
  };
}
